import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { Loader2 } from 'lucide-react';
import { ScheduledActivityResponse } from '../../types/scheduledActivity';
import { TimePeriodSectionConfig } from '../../utils/timePeriodConfig';
import { SortableActivityCard } from './SortableActivityCard';

interface TimePeriodSectionProps {
  config: TimePeriodSectionConfig;
  activities: ScheduledActivityResponse[];
  onDeleteActivity: (scheduledActivity: ScheduledActivityResponse) => void;
  deletingIds: Set<string>;
  isDragSaving?: boolean;
  isHovered?: boolean;
  isInvalidTarget?: boolean;
  viewOnly?: boolean;
}

export function TimePeriodSection({
  config,
  activities,
  onDeleteActivity,
  deletingIds,
  isDragSaving,
  isHovered,
  isInvalidTarget,
  viewOnly,
}: TimePeriodSectionProps) {
  const sectionId = `section-${config.key}`;
  const itemIds = activities.map(a => `scheduled-${a.id}`);

  // Droppable target for empty containers
  const { setNodeRef: setDroppableRef } = useDroppable({ id: sectionId });

  // Drop target visual feedback
  const dropTargetClass = isHovered
    ? isInvalidTarget
      ? 'ring-2 ring-red-300 border-red-300'
      : 'ring-2 ring-blue-400 border-blue-400'
    : '';

  return (
    <div className={`flex flex-col rounded-lg overflow-hidden border border-gray-200 transition-all duration-150 ${dropTargetClass}`}>
      {/* Header */}
      <div
        className="px-3 py-2 text-white"
        style={{ backgroundColor: config.headerColor }}
      >
        <p className="text-sm font-semibold">{config.label}</p>
        {config.hours && (
          <p className="text-xs opacity-80">{config.hours}</p>
        )}
      </div>

      {/* Content Area — droppable + sortable */}
      <div
        ref={setDroppableRef}
        className={`min-h-[100px] p-2 space-y-2 relative ${
          isHovered && isInvalidTarget ? 'bg-red-50' : 'bg-gray-50'
        }`}
      >
        {isDragSaving && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-b-lg z-10">
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          </div>
        )}

        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {activities.length === 0 ? (
            <div className="flex items-center justify-center h-full min-h-[80px]">
              <p className="text-xs text-gray-400">No activities</p>
            </div>
          ) : (
            activities.map((activity) => (
              <SortableActivityCard
                key={activity.id}
                scheduledActivity={activity}
                onDelete={onDeleteActivity}
                isDeleting={deletingIds.has(activity.id)}
                isDragDisabled={isDragSaving || viewOnly}
                viewOnly={viewOnly}
              />
            ))
          )}
        </SortableContext>
      </div>
    </div>
  );
}
